import PartnershipDTO from "@/dto/partnership/PartnershipDTO";
import { getPartnershipsBySupplierShopId } from "@/services/api/partnership/management";
import { computed, observable, when, makeObservable, actionBound } from "mobx";

/**
 * 供货商的关联关系管理【供货商使用】
 */
class SupplierPartnershipsStore {
    constructor() {
        makeObservable(this, {
            supplierPartnershipsMap: observable,
            supplierPartnershipsLoadingMap: observable,
            retailerSupplierMap: observable,
            retailerPartnershipMap: computed,
            loadEstablishedPartnerships: actionBound,
            ensureEstablishedPartnerships: actionBound,
        });
    }

    /**
     * 已建立的合作关系 ( supplierShopId => PartnershipDTO[])
     */
    supplierPartnershipsMap = new Map<number, PartnershipDTO[]>();

    /**
     * 加载已建立合作关系接口的Loading
     */
    supplierPartnershipsLoadingMap = new Map<number, boolean>();

    /**
     * 代理商与供货商的映射表 (retailerShopId => supplierShopId)
     */
    retailerSupplierMap = new Map<number, number>();

    /**
     * 代理商与合作关系的映射表 ( retailerShopId => PartnershipDTO)
     * 用于快速查找某个代理商的合作关系
     */
    get retailerPartnershipMap() {
        const mapping = new Map<number, PartnershipDTO>();

        this.supplierPartnershipsMap.forEach((partnerships) => {
            partnerships.forEach((partnership) => {
                const retailerShopId = partnership.shopId;

                mapping.set(retailerShopId, partnership);
            });
        });

        return mapping;
    }

    /**
     * 加载指定店铺的已建立合作关系
     */
    async loadEstablishedPartnerships(supplierShopId: number) {
        try {
            this.supplierPartnershipsLoadingMap.set(supplierShopId, true);
            const partnerships = await getPartnershipsBySupplierShopId(supplierShopId);
            this.supplierPartnershipsMap.set(supplierShopId, partnerships);

            const retailerToSupplierMap = new Map<number, number>(this.retailerSupplierMap);

            partnerships.forEach((partnership) => {
                retailerToSupplierMap.set(partnership.shopId, supplierShopId);
            });

            this.retailerSupplierMap = retailerToSupplierMap;
        } finally {
            this.supplierPartnershipsLoadingMap.set(supplierShopId, false);
        }

        return this.supplierPartnershipsMap.get(supplierShopId);
    }

    async ensureEstablishedPartnerships(retailerShopId: number) {
        if (this.supplierPartnershipsLoadingMap.get(retailerShopId)) {
            await when(() => !this.supplierPartnershipsLoadingMap.get(retailerShopId));
        }

        if (this.supplierPartnershipsMap.has(retailerShopId)) {
            return this.supplierPartnershipsMap.get(retailerShopId);
        }

        return await this.loadEstablishedPartnerships(retailerShopId);
    }
}

export default SupplierPartnershipsStore;
